// Shared system-sizing / SEDA / CCC rules — the SAME logic the RSC calculator
// uses, extracted so the create-sales + edit-sale forms validate + offer the same
// fixes as RSC ("only a valid config can be created").
//
// A residential config is VALID when:
//   1. ratio (kWp / EFFECTIVE inverter kW) stays under the cap — 1.42 no-battery,
//      1.8 with a battery (the battery buffers excess generation), AND
//   2. if the effective inverter exceeds the SEDA cap (1P 5kW / 3P 15kW) the config
//      carries a CCC registration add-on (RM1,000) — over-SEDA needs CCC.
//
// Fixes offered when the ratio is breached (mirror RSC):
//   • Add a 16 kWh battery (loosens the cap to 1.8), and/or
//   • Upgrade the inverter (walk the catalog upgrade chain to the smallest size that
//     clears 1.42; if that target is over-SEDA it also needs CCC), else
//   • No SEDA-safe upgrade → needs CCC or fewer panels.
import {
    parseInverterKw,
    SEDA_CAP_1P,
    SEDA_CAP_3P,
    RATIO_CAP_NO_BATT,
    RATIO_CAP_WITH_BATT,
} from '../hooks/useRscPricing';

// "Inverter Upgrade 1p5 to 1p8" → groups: fromPhase, fromKw, toPhase, toKw.
const INV_UP_RE = /inverter\s*upgrade\s*(\d)\s*p\s*(\d+(?:\.\d+)?)\s*to\s*(\d)\s*p\s*(\d+(?:\.\d+)?)/i;

// Sum a catalog price string ("150" or "500+0" base+rate) to a number.
const priceOf = (p) => String(p ?? '').replace(/[^\d.+]/g, '').split('+').reduce((s, x) => s + (Number(x) || 0), 0);

// Pull the phase-appropriate inverter kW out of a ResourcePricing row.
export function inverterKwForRow(row, phaseMode) {
    if (!row) return 0;
    const is3p = phaseMode === '3phase';
    const str = is3p
        ? (row.inverterSize3p || row.InverterSize3p)
        : (row.inverterSize || row.InverterSize);
    return parseInverterKw(str);
}

// Effective inverter kW after any applied inverter-upgrade line items (same-phase).
// The natural inverter (from the price row) is bumped to the highest applied target.
export function effectiveInverterKw(naturalKw, phaseMode, lineItems) {
    const phaseDigit = phaseMode === '3phase' ? '3' : '1';
    let eff = naturalKw;
    for (const li of (lineItems || [])) {
        const m = INV_UP_RE.exec(li.itemName || '');
        if (m && m[1] === phaseDigit && m[3] === phaseDigit) eff = Math.max(eff, parseFloat(m[4]));
    }
    return eff;
}

// Walk the catalog upgrade chain from naturalKw to the smallest inverter that brings
// kWp/kW under the no-battery cap (1.42). Returns { targetKw, cost, addonIds, sedaMax,
// overSeda, phaseDigit } or null if no chain clears it. Mirrors RSC suggestInverterUpgrade.
export function suggestInverterUpgrade({ kwp, naturalKw, phaseMode, addonCatalog }) {
    const phaseDigit = phaseMode === '3phase' ? '3' : '1';
    const sedaMax = phaseDigit === '3' ? SEDA_CAP_3P : SEDA_CAP_1P;
    if (!kwp || !naturalKw) return null;
    const stepByFrom = new Map();
    for (const a of (addonCatalog || [])) {
        const m = INV_UP_RE.exec(a.itemName || '');
        if (!m) continue;
        if (m[1] !== phaseDigit || m[3] !== phaseDigit) continue; // same-phase only
        stepByFrom.set(parseFloat(m[2]), { toKw: parseFloat(m[4]), price: priceOf(a.price), addonId: a.addonId });
    }
    let cur = naturalKw, total = 0, guard = 0; const ids = [];
    while (kwp / cur >= RATIO_CAP_NO_BATT && guard++ < 12) {
        const step = stepByFrom.get(cur);
        if (!step) break;
        cur = step.toKw; total += step.price; ids.push(step.addonId);
    }
    if (ids.length === 0 || kwp / cur >= RATIO_CAP_NO_BATT) return null; // none / can't clear
    return { targetKw: cur, cost: total, addonIds: ids, sedaMax, overSeda: cur > sedaMax, phaseDigit };
}

// Compute the sizing verdict for a residential config.
//   energy       — system size in kWp
//   phaseMode    — 'single' | '3phase'
//   inverterRow  — the matched ResourcePricing row (natural inverter size)
//   hasBattery   — true if a battery line item is present (loosens the ratio cap)
//   cccPresent   — true if a CCC add-on is already on the sale
//   lineItems    — current add-on line items (to read applied inverter upgrades)
//   addonCatalog — the add-on catalog (to suggest an inverter upgrade)
// Returns null when the inverter is unknown (caller renders/validates nothing).
export function computeSizing({ energy, phaseMode, inverterRow, hasBattery, cccPresent, lineItems, addonCatalog }) {
    const is3p = phaseMode === '3phase';
    const sedaCap = is3p ? SEDA_CAP_3P : SEDA_CAP_1P;
    const naturalKw = inverterKwForRow(inverterRow, phaseMode);
    const kwp = Number(energy) || 0;
    if (naturalKw <= 0 || kwp <= 0) return null;

    const inverterKw = effectiveInverterKw(naturalKw, phaseMode, lineItems);
    const upgraded = inverterKw > naturalKw;
    const ratio = kwp / inverterKw;
    const ratioCap = hasBattery ? RATIO_CAP_WITH_BATT : RATIO_CAP_NO_BATT;
    const ratioOk = ratio < ratioCap;
    const overSeda = inverterKw > sedaCap;
    const cccRequired = overSeda;        // over-SEDA inverter → must register CCC
    const cccSatisfied = !cccRequired || !!cccPresent;

    // A battery would clear a no-battery ratio breach (1.42 ≤ ratio < 1.8).
    const batteryResolves = !hasBattery && ratio >= RATIO_CAP_NO_BATT && ratio < RATIO_CAP_WITH_BATT;
    // Inverter-upgrade suggestion (only relevant while the ratio is breached, no batt).
    const upgrade = (!ratioOk && !hasBattery)
        ? suggestInverterUpgrade({ kwp, naturalKw, phaseMode, addonCatalog })
        : null;

    // Overall validity: ratio safe AND CCC present when required.
    const valid = ratioOk && cccSatisfied;
    let reason = '';
    if (!ratioOk) {
        const cap = hasBattery ? '1.8' : '1.42';
        const tail = hasBattery
            ? 'needs a bigger inverter or fewer panels.'
            : (upgrade ? 'add a battery or upgrade the inverter.' : (batteryResolves ? 'add a battery or use fewer panels.' : 'use fewer panels (no SEDA-safe inverter upgrade).'));
        reason = `Ratio ${ratio.toFixed(2)} exceeds ${cap} — ${tail}`;
    } else if (!cccSatisfied) {
        reason = `Inverter ${inverterKw}kW exceeds SEDA ${sedaCap}kW — add CCC (RM1,000) to register.`;
    }

    return {
        is3p, phaseDigit: is3p ? '3' : '1',
        kwp, naturalKw, inverterKw, upgraded, sedaCap,
        ratio, ratioCap, ratioOk,
        overSeda, cccRequired, cccPresent: !!cccPresent, cccSatisfied,
        batteryResolves, upgrade,
        valid, reason,
    };
}
